import { useNavigate, useSearchParams } from 'react-router-dom';
import { memo, useCallback, useEffect, useRef, type JSX } from 'react';
import { WrappedProductCard } from '@/components/features';
import {
  DoubleColumnProductGrid,
  ModalButton,
  ProductCard,
  SkeletonLoader,
} from '@/components/ui';
import useCurationStore from '@/stores/CurationStore';
import type { Product } from '@/types/Product.types';
import {
  ITEMS_UNTIL_LOAD_MORE,
  LOAD_MORE_CALLBACK_LIMIT_TIME,
  LOAD_MORE_THRESHOLD,
  NAVBAR_PADDING,
  PADDING_BETWEEN,
  PADDING_BOTTOM,
} from './DoubleColumnProductList.constants';
import styles from './DoubleColumnProductList.module.css';
import type { DoubleColumnProductListProps } from './DoubleColumnProductList.types';

const DoubleColumnProductList = ({
  products,
  className,
  isLoading = true,
  hasMore,
  onLoadMore,
  renderProductItemOverride,
  onProductClick,
  onScroll,
  isBottomNavBar = false,
  additionalFieldsTracking,
}: DoubleColumnProductListProps): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isLoadMoreInProgress = useRef<boolean>(false);
  const [params] = useSearchParams();
  const isCurations: boolean =
    location.pathname.startsWith('/create-curations') ||
    params.has('createCurations') ||
    false;
  const rafId = useRef<number | null>(null);
  const scrollableParentRef = useRef<HTMLElement | null>(null);

  // for curationCreation
  const { getSelectedProducts } = useCurationStore();
  const navigate = useNavigate();

  const triggerLoadMore = useCallback(() => {
    if (isLoadMoreInProgress.current || !hasMore || isLoading) {
      return;
    }

    isLoadMoreInProgress.current = true;
    onLoadMore();

    setTimeout(() => {
      isLoadMoreInProgress.current = false;
    }, LOAD_MORE_CALLBACK_LIMIT_TIME);
  }, [hasMore, isLoading, onLoadMore]);

  const checkIfShouldLoadMore = useCallback(
    (container: HTMLElement) => {
      if (!products || products.length === 0 || !container) return;

      // Get all product items in the container
      const productItems = container.querySelectorAll('[data-product-item]');
      if (productItems.length === 0) return;

      // Get the last few items
      const lastItems = Array.from(productItems).slice(-ITEMS_UNTIL_LOAD_MORE);

      // Check if any of the last items are approaching the viewport
      const isAnyLastItemApproaching = lastItems.some((item) => {
        const rect = item.getBoundingClientRect();

        // Check if the item is within threshold distance of the viewport
        return (
          rect.top <= window.innerHeight + LOAD_MORE_THRESHOLD &&
          rect.bottom >= -LOAD_MORE_THRESHOLD &&
          rect.left <= window.innerWidth + LOAD_MORE_THRESHOLD &&
          rect.right >= -LOAD_MORE_THRESHOLD
        );
      });

      if (
        isAnyLastItemApproaching &&
        !isLoadMoreInProgress.current &&
        hasMore &&
        !isLoading
      ) {
        triggerLoadMore();
      }
    },
    [products, hasMore, isLoading, triggerLoadMore]
  );

  const findScrollableParent = useCallback(
    (element: HTMLElement | null): HTMLElement | null => {
      while (element) {
        const style = window.getComputedStyle(element);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
          return element;
        }
        element = element.parentElement;
      }
      return null;
    },
    []
  );

  //effect to handle parent container scroll
  useEffect(() => {
    if (!containerRef.current) return;

    if (!scrollableParentRef.current) {
      scrollableParentRef.current = findScrollableParent(
        containerRef.current.parentElement
      );
    }
    const scrollableParent = scrollableParentRef.current;
    if (!scrollableParent) return;

    const handleParentScroll = (event: Event): void => {
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
      rafId.current = requestAnimationFrame(() => {
        checkIfShouldLoadMore(scrollableParent);
      });
      if (onScroll) {
        onScroll(event);
      }
    };

    scrollableParent.addEventListener('scroll', handleParentScroll);

    checkIfShouldLoadMore(scrollableParent);

    return () => {
      scrollableParent.removeEventListener('scroll', handleParentScroll);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [checkIfShouldLoadMore, findScrollableParent, onScroll]);

  const defaultRenderProductItem = useCallback(
    (product: Product): JSX.Element => {
      return (
        <>
          {isCurations ? (
            <WrappedProductCard
              key={product.id}
              product={product}
              onProductClick={onProductClick}
            />
          ) : (
            <ProductCard key={product.id} product={product} />
          )}
        </>
      );
    },
    [isCurations, onProductClick]
  );

  const renderProductItem = useCallback(
    (product: Product): JSX.Element => {
      return (
        <div data-product-item key={product.id}>
          {(renderProductItemOverride || defaultRenderProductItem)(product)}
        </div>
      );
    },
    [defaultRenderProductItem, renderProductItemOverride]
  );

  return (
    <>
      {!products || products.length === 0 ? (
        <>
          {isLoading &&
            SkeletonLoader.doubleColumnList(8, { className: styles.content })}
        </>
      ) : (
        <div
          ref={containerRef}
          style={{ paddingBottom: isBottomNavBar ? NAVBAR_PADDING : undefined }}
        >
          <DoubleColumnProductGrid
            products={products}
            className={className}
            renderProductItem={renderProductItem}
            paddingBetween={PADDING_BETWEEN}
            paddingBottom={PADDING_BOTTOM}
            additionalFieldsTracking={additionalFieldsTracking}
          />
        </div>
      )}
      {isCurations && (
        <ModalButton
          text={`View List`}
          disabled={getSelectedProducts().length === 0}
          onClick={() => navigate('/create-curations')}
        />
      )}
    </>
  );
};

export default memo(DoubleColumnProductList);
